import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const UserDetailsForm = ({
  userType,
  onNext,
  onBack,
  formData,
  setFormData,
  isProcessing = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Function to generate random test data for development
  const generateTestData = () => {
    const randomId = Math.random().toString(36).substring(2, 8);
    const testData = {
      userName: `משתמש בדיקה ${randomId}`,
      email: `testuser${randomId}@gmail.com`,
      password: "test123456",
      userPhoneNumber: `050${Math.floor(Math.random() * 9000000) + 1000000}`,
    };

    if (userType === "manager") {
      testData.userOrganizationName = `ארגון בדיקה ${randomId}`;
      testData.userField = "martial_arts";
    }

    if (userType === "coach") {
      testData.organizationCode = `ORG${randomId.toUpperCase()}`;
    }

    return testData;
  };

  // Set initial test values in development mode
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const testData = generateTestData();
      setFormData((prev) => ({ ...prev, ...testData }));
    }
  }, [userType, setFormData]);

  const validateForm = () => {
    const newErrors = {};

    // Email validation with proper regex
    if (!formData.email) {
      newErrors.email = "דואר אלקטרוני הוא שדה חובה";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "כתובת דואר אלקטרוני לא תקינה";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "סיסמה היא שדה חובה";
    } else if (formData.password.length < 6) {
      newErrors.password = "הסיסמה חייבת להיות באורך של לפחות 6 תווים";
    }

    // Name validation
    if (!formData.userName) {
      newErrors.userName = "שם הוא שדה חובה";
    }

    // Phone number validation - exactly 10 digits
    if (formData.userPhoneNumber) {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(formData.userPhoneNumber)) {
        newErrors.userPhoneNumber = "מספר טלפון חייב להיות בדיוק 10 ספרות";
      }
    }

    if (userType === "manager") {
      if (!formData.userOrganizationName) {
        newErrors.userOrganizationName = "שם הארגון הוא שדה חובה";
      }

      // Field validation - only allow martial_arts, ball_games, or other
      if (!formData.userField) {
        newErrors.userField = "תחום פעילות הוא שדה חובה";
      } else if (
        !["martial_arts", "ball_games", "other"].includes(formData.userField)
      ) {
        newErrors.userField = "אנא בחר תחום פעילות תקין";
      }
    }

    if (userType === "coach") {
      if (!formData.organizationCode) {
        newErrors.organizationCode = "קוד ארגון הוא שדה חובה";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit to 10 characters
    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
    handleInputChange("userPhoneNumber", digitsOnly);
  };

  return (
    <div>
      <h3 className="mb-4">השלם את הפרופיל שלך</h3>
      <p className="text-muted mb-4">
        {userType === "manager"
          ? "ספר לנו על עצמך ועל הארגון שלך"
          : "ספר לנו על עצמך והצטרף לארגון"}
      </p>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>שם מלא *</Form.Label>
              <Form.Control
                type="text"
                value={formData.userName || ""}
                onChange={(e) => handleInputChange("userName", e.target.value)}
                isInvalid={!!errors.userName}
                placeholder="הזן שם מלא"
                dir="rtl"
              />
              <Form.Control.Feedback type="invalid">
                {errors.userName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>דואר אלקטרוני *</Form.Label>
              <Form.Control
                type="email"
                value={formData.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                isInvalid={!!errors.email}
                placeholder="הזן דואר אלקטרוני"
                dir="rtl"
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>סיסמה *</Form.Label>
              <div className="password-input-container">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={formData.password || ""}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  isInvalid={!!errors.password}
                  placeholder="צור סיסמה"
                  dir="rtl"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <Form.Control.Feedback type="invalid">
                {errors.password}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>מספר טלפון</Form.Label>
              <Form.Control
                type="tel"
                value={formData.userPhoneNumber || ""}
                onChange={handlePhoneChange}
                isInvalid={!!errors.userPhoneNumber}
                placeholder="הזן מספר טלפון (10 ספרות)"
                dir="rtl"
                maxLength={10}
              />
              <Form.Control.Feedback type="invalid">
                {errors.userPhoneNumber}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        {userType === "manager" && (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>שם הארגון *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.userOrganizationName || ""}
                    onChange={(e) =>
                      handleInputChange("userOrganizationName", e.target.value)
                    }
                    isInvalid={!!errors.userOrganizationName}
                    placeholder="הזן שם ארגון"
                    dir="rtl"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.userOrganizationName}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>תחום פעילות *</Form.Label>
                  <Form.Select
                    value={formData.userField || ""}
                    onChange={(e) =>
                      handleInputChange("userField", e.target.value)
                    }
                    isInvalid={!!errors.userField}
                    dir="rtl"
                  >
                    <option value="">בחר תחום פעילות</option>
                    <option value="martial_arts">אומנויות לחימה</option>
                    <option value="ball_games">משחקי כדור</option>
                    <option value="other">אחר</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.userField}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {userType === "coach" && (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>קוד ארגון *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.organizationCode || ""}
                  onChange={(e) =>
                    handleInputChange("organizationCode", e.target.value)
                  }
                  isInvalid={!!errors.organizationCode}
                  placeholder="הזן קוד ארגון"
                  dir="rtl"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.organizationCode}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  שאל את המנהל שלך על קוד הארגון כדי להצטרף
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        )}

        <div className="d-flex justify-content-between mt-4">
          <Button
            variant="outline-secondary"
            onClick={onBack}
            disabled={isProcessing}
          >
            חזור
          </Button>
          <Button
            variant="primary"
            type="submit"
            className="btn-block w-100"
            disabled={isProcessing}
          >
            {isProcessing
              ? "מעבד..."
              : userType === "coach"
              ? "סיים הרשמה"
              : "המשך"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UserDetailsForm;
